import { useQuery, gql } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { useMemo } from 'react';

/**
 * GraphQL query to fetch audit item filter options for an audit session
 */
const GET_AUDIT_ITEM_FILTER_OPTIONS = gql`
  query GetAuditItemFilterOptions($communityId: ID!, $sessionId: ID!) {
    community(id: $communityId) {
      id
      auditSession(sessionId: $sessionId) {
        id
        auditItemFilterOptions {
          statuses
          labels
          severityLevels
          multipleDecisionsOptions
        }
      }
    }
  }
`;

/**
 * TypeScript types for the GraphQL response
 */
export interface AuditItemFilterOptions {
  statuses: string[];
  labels: string[];
  severityLevels: number[];
  multipleDecisionsOptions: string[];
}

export interface GetAuditItemFilterOptionsData {
  community: {
    id: string;
    auditSession: {
      id: string;
      auditItemFilterOptions: AuditItemFilterOptions;
    };
  };
}

export interface GetAuditItemFilterOptionsVariables {
  communityId: string;
  sessionId: string;
}

/**
 * Hook to fetch audit item filter options for an audit session
 *
 * This hook fetches filter options from the server-side API endpoint,
 * ensuring that available filter options remain consistent regardless
 * of applied filters (based on complete dataset, not filtered results).
 *
 * @param communityId - The ID of the community
 * @param auditSessionId - The ID of the audit session
 * @returns Object with data, loading, error (transformed), and refetch function
 */
export function useGetAuditItemFilterOptions(communityId: string, auditSessionId: string) {
  const { data, loading, error, refetch } = useQuery<GetAuditItemFilterOptionsData, GetAuditItemFilterOptionsVariables>(GET_AUDIT_ITEM_FILTER_OPTIONS, {
    variables: { communityId, sessionId: auditSessionId },
    skip: !communityId || !auditSessionId,
    errorPolicy: 'all', // Return partial data even if there are errors
    fetchPolicy: 'cache-first', // Cache filter options since they're stable
  });

  // Transform error using the same pattern as other audit hooks
  const domainError = useMemo(() => {
    if (!error) return undefined;

    // Map GraphQL error to user-friendly format
    const mappedError = transformError(error);

    // Return error with onRetry function for compatibility with existing UI
    return {
      ...mappedError,
      onRetry: () => refetch(),
    };
  }, [error, refetch]);

  return {
    data,
    loading,
    error: domainError,
    refetch,
  };
}
